/**
 * Scene commands: pure functions (scene in, scene out) behind every edit, so undo/redo is a history of scene
 * snapshots and each command is unit-tested without a browser. Commands that move elements between parents take a
 * `Geo` (the canvas geometry) to keep each element where it is on screen.
 */
import type { Asset, Element, Field, GroupElement, Layout, Scene, Track } from '@prism-graphics/scene-schema';
import { uniqueId } from './ids.ts';
import { allIds, findElement, indexIn, insertInto, isContainer, mapElements, parentOf, pathTo, removeFromTree, topmost, updateElement, walk } from './tree.ts';

export type Point = { x: number; y: number };
export type Rect = { x: number; y: number; width: number; height: number };

/** Canvas geometry the commands need (from the static render, or modelGeo() in tests). */
export interface Geo {
  /** Canvas position of the element's box top-left (as placed, ignoring its own rotation and scale). */
  topLeft(id: string): Point | undefined;
  /** Where children of `id` (null: the canvas) are positioned from: the padding-box origin, in canvas px. */
  contentOrigin(id: string | null): Point;
  /** Visual axis-aligned bounds in canvas px. */
  bounds(id: string): Rect | undefined;
  /** Size of the box children of `id` (null: the canvas) are anchored in: the padding box. */
  contentSize?(id: string | null): { width: number; height: number };
  /** The element's own layout size (before transforms). */
  size?(id: string): { width: number; height: number } | undefined;
}

/** Geometry from the model alone: left/top anchors, numeric sizes (auto counts as 0). For tests and fallbacks. */
export function modelGeo(scene: Scene): Geo {
  const topLeft = (id: string): Point | undefined => {
    const path = pathTo(scene, id);
    if (path.length === 0) return undefined;
    let x = 0;
    let y = 0;
    for (const pid of path) {
      const el = findElement(scene, pid)!;
      x += el.transform.x;
      y += el.transform.y;
    }
    return { x, y };
  };
  return {
    topLeft,
    contentOrigin: (id) => (id === null ? { x: 0, y: 0 } : (topLeft(id) ?? { x: 0, y: 0 })),
    bounds: (id) => {
      const p = topLeft(id);
      const el = findElement(scene, id);
      if (!p || !el) return undefined;
      return { x: p.x, y: p.y, width: typeof el.size.width === 'number' ? el.size.width : 0, height: typeof el.size.height === 'number' ? el.size.height : 0 };
    },
  };
}

// ---------------------------------------------------------------------------------------------------------------
// Assets

/** Add an asset entry to the scene if it is not there (a clashing file name gets the hash prefix). */
export function ensureAsset(scene: Scene, asset: Asset): Scene {
  if (scene.assets.some((a) => a.hash === asset.hash)) return scene;
  const dir = (a: Asset) => (a.kind === 'font' ? 'fonts' : 'media');
  let fileName = asset.fileName;
  if (scene.assets.some((a) => dir(a) === dir(asset) && a.fileName === fileName)) {
    const dot = fileName.lastIndexOf('.');
    fileName = `${fileName.slice(0, dot)}-${asset.hash.slice(7, 15)}${fileName.slice(dot)}`;
  }
  return { ...scene, assets: [...scene.assets, { ...asset, fileName }] };
}

/** Remove asset entries nothing references any more. */
export function pruneAssets(scene: Scene): Scene {
  const used = new Set<string>();
  walk(scene.elements, (el) => {
    if (el.type === 'text') used.add(el.style.font);
    if (el.type === 'image' || el.type === 'video') used.add(el.asset);
  });
  for (const f of scene.fields) if (f.type === 'image-choice') f.choices.forEach((c) => used.add(c));
  if (scene.canvas.background?.asset) used.add(scene.canvas.background.asset);
  const assets = scene.assets.filter((a) => used.has(a.hash));
  return assets.length === scene.assets.length ? scene : { ...scene, assets };
}

// ---------------------------------------------------------------------------------------------------------------
// Adding, editing, removing

export interface InsertAt {
  parentId: string | null;
  index?: number;
}

export function addElements(scene: Scene, items: readonly Element[], at: InsertAt = { parentId: null }): Scene {
  return { ...scene, elements: insertInto(scene.elements, at.parentId, at.index, items) };
}

export function patchElement(scene: Scene, id: string, patch: Partial<Element> | ((el: Element) => Element)): Scene {
  return updateElement(scene, id, (el) => (typeof patch === 'function' ? patch(el) : ({ ...el, ...patch } as Element)));
}

export function setTransform(scene: Scene, id: string, patch: Partial<Element['transform']>): Scene {
  return updateElement(scene, id, (el) => ({ ...el, transform: { ...el.transform, ...patch } }) as Element);
}

export function setSize(scene: Scene, id: string, patch: Partial<Element['size']>): Scene {
  return updateElement(scene, id, (el) => {
    const size = { ...el.size, ...patch };
    for (const k of Object.keys(size) as Array<keyof typeof size>) if (size[k] === undefined) delete size[k];
    return { ...el, size } as Element;
  });
}

/**
 * Remove fields' bindings, timeline tracks and layout overrides (element and keyframe overrides, ADR 0037) that point
 * at elements that no longer exist.
 */
export function dropDanglingReferences(scene: Scene): Scene {
  const ids = allIds(scene);
  const keepTrack = (t: Track) => ids.has(t.element);
  const phase = <P extends { tracks: Track[] }>(p: P): P => (p.tracks.every(keepTrack) ? p : { ...p, tracks: p.tracks.filter(keepTrack) });
  const fields = scene.fields.map((f) => (f.bindings.every((b) => ids.has(b.element)) ? f : ({ ...f, bindings: f.bindings.filter((b) => ids.has(b.element)) } as Field)));
  const t = scene.timeline;
  const timeline = {
    ...t,
    in: phase(t.in),
    out: phase(t.out),
    steps: t.steps.map((s) => ({ ...s, ...(s.enter ? { enter: phase(s.enter) } : {}), ...(s.loop ? { loop: phase(s.loop) } : {}) })),
    update: {
      ...t.update,
      tracks: t.update.tracks.filter(keepTrack),
      fields: Object.fromEntries(Object.entries(t.update.fields).map(([k, v]) => [k, v.mode === 'phase' ? { ...v, phase: phase(v.phase) } : v])),
    },
    customActions: t.customActions.map((a) => ({ ...a, phase: phase(a.phase) })),
  };
  return { ...scene, fields, timeline, ...(scene.layouts ? { layouts: scene.layouts.map((l) => dropDanglingOverrides(l, ids)) } : {}) };
}

/** A layout without the overrides of elements not in `ids` (a keyframe override's key ends "/<element>/<property>"). */
function dropDanglingOverrides(l: Layout, ids: ReadonlySet<string>): Layout {
  const elementOf = (key: string) => key.split('/').at(-2) ?? '';
  const overrides = l.overrides && Object.fromEntries(Object.entries(l.overrides).filter(([id]) => ids.has(id)));
  const keyframes = l.keyframes && Object.fromEntries(Object.entries(l.keyframes).filter(([key]) => ids.has(elementOf(key))));
  const sameSize = (a: object | undefined, b: object | undefined) => Object.keys(a ?? {}).length === Object.keys(b ?? {}).length;
  if (sameSize(overrides, l.overrides) && sameSize(keyframes, l.keyframes)) return l;
  const { overrides: _o, keyframes: _k, ...rest } = l;
  return { ...rest, ...(overrides && Object.keys(overrides).length ? { overrides } : {}), ...(keyframes && Object.keys(keyframes).length ? { keyframes } : {}) };
}

export function deleteElements(scene: Scene, ids: readonly string[]): Scene {
  if (ids.length === 0) return scene;
  const next = { ...scene, elements: removeFromTree(scene.elements, new Set(ids)) };
  return pruneAssets(dropDanglingReferences(next));
}

/** Move elements by a canvas-px delta each (their x/y translation). */
export function moveElements(scene: Scene, deltas: ReadonlyMap<string, Point>): Scene {
  if (deltas.size === 0) return scene;
  const elements = mapElements(scene.elements, (el) => {
    const d = deltas.get(el.id);
    if (!d || (d.x === 0 && d.y === 0)) return el;
    return { ...el, transform: { ...el.transform, x: round(el.transform.x + d.x), y: round(el.transform.y + d.y) } } as Element;
  });
  return elements === scene.elements ? scene : { ...scene, elements };
}

export function moveBy(scene: Scene, ids: readonly string[], dx: number, dy: number): Scene {
  return moveElements(scene, new Map(topmost(scene, ids).map((id) => [id, { x: dx, y: dy }])));
}

const round = (n: number) => Math.round(n * 100) / 100;

/** Fraction of a size change that moves the box's left (top) edge for an anchor: left 0, centre ½, right 1. */
export function anchorFactor(anchor: string | undefined): number {
  return anchor === 'center' || anchor === 'middle' ? 0.5 : anchor === 'right' || anchor === 'bottom' ? 1 : 0;
}

/**
 * How far an element's translation moves when its box changes from w0×h0 to w1×h1 while the point at fraction
 * (fx, fy) of the box stays where it is on screen, under its own rotation, scale and transform origin. With no
 * rotation and scale 1 this is just (fx·(w0−w1), fy·(h0−h1)).
 */
export function resizeShift(t: Element['transform'], w0: number, h0: number, w1: number, h1: number, fx: number, fy: number): Point {
  const a = (t.rotation * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  // (F0 − O0) − (F1 − O1) in the box's own axes, scaled, then rotated; plus the move of the origin O0 − O1.
  const lx = (fx - t.originX) * (w0 - w1) * t.scaleX;
  const ly = (fy - t.originY) * (h0 - h1) * t.scaleY;
  return { x: t.originX * (w0 - w1) + cos * lx - sin * ly, y: t.originY * (h0 - h1) + sin * lx + cos * ly };
}

/**
 * Resize to a new box: `dLeft`/`dTop` are the change of the left/top edge in the element's own axes (non-zero when
 * the left/top edge is dragged, so the right/bottom edge stays). Rotation-aware: the opposite edge stays where it is
 * on screen whatever the rotation and scale. Anchors are respected (a right-anchored element's x compensates).
 */
export function resizeElement(scene: Scene, id: string, box: { width: number; height: number; dLeft: number; dTop: number }): Scene {
  return updateElement(scene, id, (el) => {
    const w0 = typeof el.size.width === 'number' ? el.size.width : box.width + box.dLeft;
    const h0 = typeof el.size.height === 'number' ? el.size.height : box.height + box.dTop;
    const width = Math.max(1, round(box.width));
    const height = Math.max(1, round(box.height));
    const kx = anchorFactor(el.anchor?.horizontal);
    const ky = anchorFactor(el.anchor?.vertical);
    const size = { ...el.size, width: el.anchor?.horizontal === 'stretch' ? el.size.width : width } as Element['size'];
    if (el.type !== 'line') size.height = el.anchor?.vertical === 'stretch' ? el.size.height : height;
    const h1 = el.type === 'line' ? h0 : height;
    const d = resizeShift(el.transform, w0, h0, width, h1, box.dLeft !== 0 ? 1 : 0, box.dTop !== 0 ? 1 : 0);
    const x = el.transform.x + d.x + kx * (width - w0);
    const y = el.transform.y + d.y + (el.type === 'line' ? 0 : ky * (height - h0));
    return { ...el, size, transform: { ...el.transform, x: round(x), y: round(y) } } as Element;
  });
}

const BASE: Record<string, number> = { left: 0, top: 0, stretch: 0, center: 0.5, middle: 0.5, right: 1, bottom: 1 };

/**
 * Change an element's anchoring (edges, or parent ↔ canvas) without moving it: x/y are recomputed so the element
 * stays where it is. A stretched side keeps its x/y (the box follows the container).
 */
export function setAnchor(scene: Scene, id: string, next: NonNullable<Element['anchor']>, geo: Geo): Scene {
  const el = findElement(scene, id);
  if (!el) return scene;
  const prev = el.anchor ?? { horizontal: 'left', vertical: 'top', to: 'parent' as const };
  const parentId = parentOf(scene, id)?.id ?? null;
  const size = geo.size?.(id) ?? { width: typeof el.size.width === 'number' ? el.size.width : 0, height: typeof el.size.height === 'number' ? el.size.height : 0 };
  const canvas = { width: scene.canvas.width, height: scene.canvas.height };
  const container = (to: 'parent' | 'canvas') => {
    if (to === 'canvas' || parentId === null) return { origin: { x: 0, y: 0 }, size: canvas };
    return { origin: geo.contentOrigin(parentId), size: geo.contentSize?.(parentId) ?? canvas };
  };
  const a = container(prev.to);
  const b = container(next.to);
  const shift = (axis: 'x' | 'y', from: string, to: string) => {
    if (to === 'stretch' && from === 'stretch') return 0;
    const len = axis === 'x' ? 'width' : 'height';
    const posA = a.origin[axis] + (BASE[from] ?? 0) * (a.size[len] - (from === 'stretch' ? a.size[len] : size[len]));
    const posB = b.origin[axis] + (BASE[to] ?? 0) * (b.size[len] - (to === 'stretch' ? b.size[len] : size[len]));
    return posA - posB;
  };
  const dx = shift('x', prev.horizontal, next.horizontal);
  const dy = shift('y', prev.vertical, next.vertical);
  return updateElement(scene, id, (e) => ({ ...e, anchor: next, transform: { ...e.transform, x: round(e.transform.x + dx), y: round(e.transform.y + dy) } }) as Element);
}

// ---------------------------------------------------------------------------------------------------------------
// Order and hierarchy

/** Move an element within its parent: +1 forward (towards the front), -1 backward, 'front', 'back'. */
export function reorder(scene: Scene, id: string, to: 1 | -1 | 'front' | 'back'): Scene {
  const parent = parentOf(scene, id);
  const list = parent ? parent.children : scene.elements;
  const i = list.findIndex((e) => e.id === id);
  if (i < 0) return scene;
  const j = to === 'front' ? list.length - 1 : to === 'back' ? 0 : Math.max(0, Math.min(list.length - 1, i + to));
  if (i === j) return scene;
  const next = [...list];
  const [el] = next.splice(i, 1);
  next.splice(j, 0, el!);
  if (!parent) return { ...scene, elements: next };
  return updateElement(scene, parent.id, (p) => ({ ...p, children: next }) as Element);
}

/** Place an element where it is on screen inside a new parent (left/top anchored), or at 0,0 in a stack. */
function placeIn(scene: Scene, el: Element, parentId: string | null, geo: Geo): Element {
  const parent = parentId ? findElement(scene, parentId) : undefined;
  const { anchor: _a, ...rest } = el;
  if (parent && parent.type === 'group' && parent.layout.mode === 'stack') return { ...rest, transform: { ...el.transform, x: 0, y: 0 } } as Element;
  const p = geo.topLeft(el.id);
  if (!p) return el;
  const o = geo.contentOrigin(parentId);
  return { ...rest, transform: { ...el.transform, x: round(p.x - o.x), y: round(p.y - o.y) } } as Element;
}

/**
 * Move an element to another parent (null: the top level) at `index` in its children (paint order), keeping its
 * place on screen. Refuses to move a group into itself.
 */
export function reparent(scene: Scene, id: string, parentId: string | null, index: number, geo: Geo): Scene {
  const el = findElement(scene, id);
  if (!el) return scene;
  if (parentId !== null && (parentId === id || pathTo(scene, parentId).includes(id))) return scene;
  const target = parentId ? findElement(scene, parentId) : undefined;
  if (parentId !== null && !isContainer(target)) return scene;
  const oldParent = parentOf(scene, id)?.id ?? null;
  const oldIndex = indexIn(scene, id);
  const placed = oldParent === parentId ? el : placeIn(scene, el, parentId, geo);
  const without = removeFromTree(scene.elements, new Set([id]));
  // Removing it first shifts later siblings in the same parent down by one.
  const at = oldParent === parentId && oldIndex < index ? index - 1 : index;
  return { ...scene, elements: insertInto(without, parentId, at, [placed]) };
}

function boundsOf(rects: Rect[]): Rect {
  const x = Math.min(...rects.map((r) => r.x));
  const y = Math.min(...rects.map((r) => r.y));
  const r = Math.max(...rects.map((q) => q.x + q.width));
  const b = Math.max(...rects.map((q) => q.y + q.height));
  return { x, y, width: r - x, height: b - y };
}

/**
 * Group siblings into a new absolute group (or clip mask) sized to their bounds, keeping them in place. Elements
 * with different parents are grouped in the parent of the first. Returns the new group's id.
 */
export function groupElements(scene: Scene, ids: readonly string[], geo: Geo, kind: 'group' | 'clipMask' = 'group'): { scene: Scene; id: string | undefined } {
  const chosen = topmost(scene, ids).filter((id) => findElement(scene, id));
  if (chosen.length === 0) return { scene, id: undefined };
  const parentId = parentOf(scene, chosen[0]!)?.id ?? null;
  const siblings = parentId ? (findElement(scene, parentId) as GroupElement).children : scene.elements;
  // Keep paint order: the group goes where the front-most chosen sibling was.
  const order = [...chosen].sort((a, b) => indexIn(scene, a) - indexIn(scene, b));
  const rects = order.map((id) => geo.bounds(id)).filter((r): r is Rect => r !== undefined);
  const bb = rects.length ? boundsOf(rects) : { x: 0, y: 0, width: 100, height: 100 };
  const origin = geo.contentOrigin(parentId);
  const id = uniqueId(kind === 'group' ? 'group' : 'mask', allIds(scene));
  const names = new Set<string>();
  walk(scene.elements, (el) => names.add(el.name));
  const label = kind === 'group' ? 'Group' : 'Mask';
  let n = 1;
  while (names.has(`${label} ${n}`)) n++;
  const children = order.map((cid) => {
    const el = findElement(scene, cid)!;
    const p = geo.topLeft(cid) ?? { x: bb.x, y: bb.y };
    const { anchor: _a, ...rest } = el;
    return { ...rest, transform: { ...el.transform, x: round(p.x - bb.x), y: round(p.y - bb.y) } } as Element;
  });
  const common = {
    id,
    name: `${label} ${n}`,
    locked: false,
    visible: true,
    transform: { x: round(bb.x - origin.x), y: round(bb.y - origin.y), rotation: 0, scaleX: 1, scaleY: 1, originX: 0.5, originY: 0.5, opacity: 1 },
    size: { width: round(bb.width), height: round(bb.height) },
    children,
  };
  const group: Element =
    kind === 'group'
      ? { ...common, type: 'group', layout: { mode: 'absolute' }, style: {}, clipContent: false }
      : { ...common, type: 'clipMask', shape: 'rect' };
  const lastIndex = Math.max(...order.map((cid) => siblings.findIndex((e) => e.id === cid)));
  const insertAt = lastIndex - (order.length - 1);
  const without = removeFromTree(scene.elements, new Set(order));
  return { scene: { ...scene, elements: insertInto(without, parentId, insertAt, [group]) }, id };
}

/** Replace groups by their children (kept in place, at the group's position in the paint order). */
export function ungroup(scene: Scene, ids: readonly string[], geo: Geo): { scene: Scene; ids: string[] } {
  let s = scene;
  const released: string[] = [];
  for (const gid of ids) {
    const g = findElement(s, gid);
    if (!isContainer(g)) continue;
    const parentId = parentOf(s, gid)?.id ?? null;
    const index = indexIn(s, gid);
    const children = g.children.map((c) => placeIn(s, c, parentId, geo));
    released.push(...children.map((c) => c.id));
    const without = removeFromTree(s.elements, new Set([gid]));
    s = dropDanglingReferences({ ...s, elements: insertInto(without, parentId, index, children) });
  }
  return { scene: s, ids: released };
}

// ---------------------------------------------------------------------------------------------------------------
// Duplicate and paste

/** Deep-copy elements with fresh ids (unique in `scene`). Returns the copies and the old → new id map. */
export function cloneWithNewIds(scene: Scene, elements: readonly Element[]): { elements: Element[]; ids: Map<string, string> } {
  const taken = allIds(scene);
  const ids = new Map<string, string>();
  const copy = (el: Element): Element => {
    const prefix = el.id.replace(/\d+$/, '') || el.type;
    const nid = uniqueId(prefix, taken);
    taken.add(nid);
    ids.set(el.id, nid);
    const c = { ...structuredClone(el), id: nid } as Element;
    if (isContainer(c)) (c as GroupElement).children = (el as GroupElement).children.map(copy);
    return c;
  };
  return { elements: elements.map(copy), ids };
}

/**
 * Duplicate elements next to the originals, offset by `offset` px. Returns the copies' ids. In a graphic with layouts
 * the copies take the originals' overrides (the same offset on an overridden position), so each layout shows its copy
 * where it shows the original.
 */
export function duplicate(scene: Scene, ids: readonly string[], offset = 20): { scene: Scene; ids: string[] } {
  let s = scene;
  const out: string[] = [];
  const copied = new Map<string, string>();
  const tops = new Set<string>();
  for (const id of topmost(scene, ids)) {
    const el = findElement(s, id);
    if (!el) continue;
    // Copies are not bound to fields: a field keeps driving the original only.
    const { elements: [copy], ids: map } = cloneWithNewIds(s, [el]);
    const moved = { ...copy!, transform: { ...copy!.transform, x: copy!.transform.x + offset, y: copy!.transform.y + offset } } as Element;
    s = addElements(s, [moved], { parentId: parentOf(s, id)?.id ?? null, index: indexIn(s, id) + 1 });
    out.push(moved.id);
    for (const [from, to] of map) copied.set(from, to);
    tops.add(moved.id);
  }
  if (s.layouts && copied.size) s = { ...s, layouts: s.layouts.map((l) => copyOverrides(l, copied, tops, offset)) };
  return { scene: s, ids: out };
}

/** The copies (`copied`: original → copy id) take the originals' overrides; only `tops` (the copied roots) are offset. */
function copyOverrides(l: Layout, copied: ReadonlyMap<string, string>, tops: ReadonlySet<string>, offset: number): Layout {
  if (!l.overrides) return l;
  const overrides = { ...l.overrides };
  for (const [from, to] of copied) {
    const o = l.overrides[from];
    if (!o) continue;
    const c = structuredClone(o);
    if (tops.has(to) && c.transform) {
      if (c.transform.x !== undefined) c.transform.x += offset;
      if (c.transform.y !== undefined) c.transform.y += offset;
    }
    overrides[to] = c;
  }
  return { ...l, overrides };
}

// ---------------------------------------------------------------------------------------------------------------
// Canvas

export function setCanvas(scene: Scene, patch: Partial<Pick<Scene['canvas'], 'width' | 'height' | 'frameRate' | 'background'>>): Scene {
  const canvas = { ...scene.canvas, ...patch };
  if (patch.background === undefined && 'background' in patch) delete canvas.background;
  return pruneAssets({ ...scene, canvas });
}

export function setMeta(scene: Scene, patch: Partial<Scene['meta']>): Scene {
  return { ...scene, meta: { ...scene.meta, ...patch } };
}
